import React, { useMemo, useRef, useState } from "react";
import {
  Box,
  Skeleton,
  Stack,
  TablePagination,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  AlertTriangle,
  CalendarDays,
  CheckCircle,
  ClipboardList,
  Clock,
  SlidersHorizontal,
  FolderOpen,
  MessageSquare,
  Settings,
  Tag,
  User,
  Building2,
  Users,
  Link2,
} from "lucide-react";

import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../../components/Card";
import { Badge } from "../../../components/Badge";
import { TASK_STATE_BADGES } from "../../Projects/stateBadges";
import { Button } from "../../../components/Button";
import { EmptyStateMessage } from "../../../components/EmptyStateMessage";
import { IncidentFiltersModal } from "../../../components/IncidentFiltersModal";
import { UnifiedModal } from "../../../components/UnifiedModal";
import { FilterSwitchGroup } from "../../../components/FilterSwitchGroup";
import { useAuth } from "../../../components/contexts/AuthContext";
import { getData } from "../../../components/hooks/getData";
import { useTheme } from "../../../theme/useTheme";
import apiClient from "../../../api-client";
import SuccessPopup from "../../../components/SuccessPopup";
import { DocumentationLinkList } from "../../../components/DocumentationLinkList";

const TASK_STATE_FILTERS = [
  { value: "all", label: "Todos" },
  { value: "planeado", label: "Planeado" },
  { value: "en_desarrollo", label: "En progreso" },
  { value: "finalizado", label: "Finalizado" },
  { value: "no_necesario", label: "No necesario" },
];

const TASK_ORDERING_OPTIONS = [
  { value: "deadline", label: "Deadline" },
  { value: "created_at", label: "Creado" },
  { value: "state", label: "Estado" },
];

// Filter configuration similar to notifications
const TASK_STATUS_FILTERS = {
  pending: { label: "Pendientes", states: null }, // null = todas las tareas (backend no filtra)
  completed: { label: "Finalizadas", states: ["finalizado"] },
};

const TaskIndicatorDot = ({ theme, sx = {} }) => (
  <Box
    sx={{
      width: "5px",
      height: "5px",
      borderRadius: "50%",
      backgroundColor: theme.palette.primary.main,
      flexShrink: 0,
      ...sx,
    }}
  />
);

// Helper to format message content with mentions
const formatMentionContent = (content) => {
  if (!content) return content;
  // Replace mention format @[Name](user:id) with @Name
  const mentionRegex = /@\[([^\]]+)\]\(user:\d+\)/g;
  return content.replace(mentionRegex, (match, name) => {
    return `@${name.trim()}`;
  });
};

const getTaskTitle = (task = {}) => {
  const rawTitle = task.title || task.name || task.task_title || "Tarea sin título";
  return formatMentionContent(rawTitle);
};

const getTaskDescription = (task = {}) => {
  const rawDescription = task.description || task.summary || task.message_preview || "";
  return formatMentionContent(rawDescription);
};

const getPhaseName = (task = {}) =>
  task.phase?.name || task.phase_name || task.phase_title || null;

const getProjectName = (task = {}) =>
  task.project?.name || task.project_name || null;

const getClientName = (task = {}) =>
  task.client?.name || task.client_name || null;

const getAgencyName = (task = {}) =>
  task.agency?.name || task.agency_name || null;

const getTaskState = (task = {}) => task.state || task.status || "planeado";

const DEFAULT_ORDERING_VALUE = "deadline";

const getDefaultOrderingOption = () => {
  const option = TASK_ORDERING_OPTIONS.find(
    (item) => item.value === DEFAULT_ORDERING_VALUE
  );
  return option ? { ...option } : null;
};

const createEmptyAdvancedFilters = () => ({
  state: null,
  ordering: getDefaultOrderingOption(),
  deadlineAfter: "",
  deadlineBefore: "",
  client: null,
  agency: null,
});

const cloneFilterOption = (option) => (option ? { ...option } : null);

const cloneAdvancedFilters = (filters) => ({
  state: cloneFilterOption(filters.state),
  ordering: filters.ordering
    ? { ...filters.ordering }
    : getDefaultOrderingOption(),
  deadlineAfter: filters.deadlineAfter || "",
  deadlineBefore: filters.deadlineBefore || "",
  client: cloneFilterOption(filters.client),
  agency: cloneFilterOption(filters.agency),
});

const buildInitialAdvancedFilters = (
  globalAgencyOption,
  globalClientOption
) => {
  const base = createEmptyAdvancedFilters();
  if (globalAgencyOption) {
    base.agency = { ...globalAgencyOption, isLocked: true };
  }
  if (globalClientOption) {
    base.client = { ...globalClientOption, isLocked: true };
  }
  return base;
};

const ensureGlobalLock = (filters, field, option) => {
  if (!filters) return filters;
  if (option) {
    if (filters[field]?.value === option.value && filters[field]?.isLocked) {
      return filters;
    }
    return {
      ...filters,
      [field]: { ...option, isLocked: true },
    };
  }
  if (filters[field]?.isLocked) {
    const next = { ...filters };
    next[field] = null;
    return next;
  }
  return filters;
};

const normalizeApiList = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.results)) return payload.results;
  return [];
};

const serializeFilters = (advancedFilters, selectedTaskFilter) => {
  // Para "pending", no aplicamos filtro de estado (backend devuelve todas las tareas)
  const taskFilterStates =
    selectedTaskFilter === "pending"
      ? null
      : getTaskStatesFromFilter(selectedTaskFilter);

  return {
    state: advancedFilters.state?.value ?? taskFilterStates,
    ordering: advancedFilters.ordering?.value ?? DEFAULT_ORDERING_VALUE,
    deadlineAfter: advancedFilters.deadlineAfter || "",
    deadlineBefore: advancedFilters.deadlineBefore || "",
    client: advancedFilters.client?.value ?? null,
    agency: advancedFilters.agency?.value ?? null,
  };
};

const getTaskStatesFromFilter = (filterKey) => {
  const filterConfig = TASK_STATUS_FILTERS[filterKey];
  return filterConfig ? filterConfig.states : null;
};

const countActiveAdvancedFilters = (advancedFilters) => {
  let count = 0;
  if (advancedFilters.state) count += 1;
  if (
    advancedFilters.ordering &&
    advancedFilters.ordering.value &&
    advancedFilters.ordering.value !== DEFAULT_ORDERING_VALUE
  ) {
    count += 1;
  }
  if (advancedFilters.deadlineAfter) count += 1;
  if (advancedFilters.deadlineBefore) count += 1;
  if (advancedFilters.client) count += 1;
  if (advancedFilters.agency) count += 1;
  return count;
};

const formatShortDate = (value) => {
  if (!value) return "Sin fecha";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Sin fecha";
  return date.toLocaleDateString("es-ES", {
    day: "2-digit",
    month: "short",
  });
};

const buildDeadlineInfo = (value) => {
  if (!value) {
    return { label: "Sin deadline", relative: "", tone: "muted" };
  }

  const deadline = new Date(value);
  if (Number.isNaN(deadline.getTime())) {
    return { label: "Fecha inválida", relative: "", tone: "muted" };
  }

  const now = new Date();
  const diffDays = Math.ceil(
    (deadline.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)
  );

  if (diffDays < 0) {
    return {
      label: formatShortDate(value),
      relative: "Vencida",
      tone: "danger",
    };
  }

  if (diffDays === 0) {
    return {
      label: formatShortDate(value),
      relative: "Hoy",
      tone: "warning",
    };
  }

  if (diffDays === 1) {
    return {
      label: formatShortDate(value),
      relative: "Mañana",
      tone: "warning",
    };
  }

  return {
    label: formatShortDate(value),
    relative: `En ${diffDays} días`,
    tone: "neutral",
  };
};

const MONTH_NAMES = [
  "enero",
  "febrero",
  "marzo",
  "abril",
  "mayo",
  "junio",
  "julio",
  "agosto",
  "septiembre",
  "octubre",
  "noviembre",
  "diciembre",
];

const capitalize = (value) =>
  value ? value.charAt(0).toUpperCase() + value.slice(1) : value;

const formatDeadlineDayMonth = (value) => {
  if (!value) return "Sin deadline";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Sin deadline";
  const day = date.getDate();
  const month = capitalize(MONTH_NAMES[date.getMonth()] || "");
  return `${day} de ${month}`;
};

const formatFullDate = (value) => {
  if (!value) return "Sin fecha";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Sin fecha";
  return date.toLocaleDateString("es-ES", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatDateOnly = (value) => {
  if (!value) return "Sin fecha";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Sin fecha";
  return date.toLocaleDateString("es-ES", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const getRemainingDaysText = (deadlineInfo) => {
  if (!deadlineInfo.relative) return "";

  if (deadlineInfo.relative === "Vencida") return "Vencida";
  if (deadlineInfo.relative === "Hoy") return "Hoy";
  if (deadlineInfo.relative === "Mañana") return "Mañana";

  // Para casos como "En X días", convertir a "Quedan X días"
  const match = deadlineInfo.relative.match(/En (\d+) días/);
  if (match) {
    const days = parseInt(match[1]);
    return `Quedan ${days} día${days !== 1 ? "s" : ""}`;
  }

  return deadlineInfo.relative;
};

export const MyTasksPanel = ({
  interceptorReady,
  dashboardRefreshKey = 0,
  selectedFilter = null,
  selectedFilterData = null,
  filterRefresh = 0,
  paginationInHeader = false,
  maxHeight = "calc(100vh - 280px)",
}) => {
  const { theme, neutrals } = useTheme();
  const { user: currentUser } = useAuth();
  const [selectedTaskId, setSelectedTaskId] = useState(null);
  const [taskDetailKey, setTaskDetailKey] = useState(0);
  const [tasksRefreshKey, setTasksRefreshKey] = useState(0);
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [selectedTaskFilter, setSelectedTaskFilter] = useState("pending");
  const [taskPage, setTaskPage] = useState(0);
  const [taskPageSize, setTaskPageSize] = useState(10);
  const [tasksTotalCount, setTasksTotalCount] = useState(0);
  const [successPopup, setSuccessPopup] = useState({
    isOpen: false,
    message: "",
    error: false,
  });

  const isSuperAdmin = currentUser?.role === "superadmin";
  const isAgencyScopedUser = ["agency_admin", "consultant"].includes(
    currentUser?.role
  );

  const globalAgencyOption =
    isSuperAdmin && selectedFilter && selectedFilterData?.name
      ? { value: selectedFilter, label: selectedFilterData.name }
      : null;

  const globalClientOption =
    isAgencyScopedUser && selectedFilter && selectedFilterData?.name
      ? { value: selectedFilter, label: selectedFilterData.name }
      : null;

  const [appliedAdvancedFilters, setAppliedAdvancedFilters] = useState(() =>
    buildInitialAdvancedFilters(globalAgencyOption, globalClientOption)
  );
  const [draftAdvancedFilters, setDraftAdvancedFilters] = useState(() =>
    buildInitialAdvancedFilters(globalAgencyOption, globalClientOption)
  );

  React.useEffect(() => {
    setAppliedAdvancedFilters((prev) =>
      ensureGlobalLock(prev, "agency", globalAgencyOption)
    );
    setDraftAdvancedFilters((prev) =>
      ensureGlobalLock(prev, "agency", globalAgencyOption)
    );
  }, [globalAgencyOption]);

  React.useEffect(() => {
    setAppliedAdvancedFilters((prev) =>
      ensureGlobalLock(prev, "client", globalClientOption)
    );
    setDraftAdvancedFilters((prev) =>
      ensureGlobalLock(prev, "client", globalClientOption)
    );
  }, [globalClientOption]);

  const serializedFilters = useMemo(
    () => serializeFilters(appliedAdvancedFilters, selectedTaskFilter),
    [appliedAdvancedFilters, selectedTaskFilter]
  );

  const filtersKey = useMemo(
    () =>
      JSON.stringify({ ...serializedFilters, taskFilter: selectedTaskFilter }),
    [serializedFilters, selectedTaskFilter]
  );

  const queryString = useMemo(() => {
    const params = new URLSearchParams();
    if (serializedFilters.state)
      params.append("state", serializedFilters.state);
    if (serializedFilters.deadlineAfter)
      params.append("deadline_after", serializedFilters.deadlineAfter);
    if (serializedFilters.deadlineBefore)
      params.append("deadline_before", serializedFilters.deadlineBefore);
    if (
      serializedFilters.ordering &&
      serializedFilters.ordering !== DEFAULT_ORDERING_VALUE
    ) {
      params.append("ordering", serializedFilters.ordering);
    }
    if (serializedFilters.client)
      params.append("client", serializedFilters.client);
    if (serializedFilters.agency)
      params.append("agency", serializedFilters.agency);
    params.append("page", (taskPage + 1).toString());
    params.append("page_size", taskPageSize.toString());
    return params.toString();
  }, [serializedFilters, taskPage, taskPageSize]);

  const shouldFetchModalAgencies = isFilterModalOpen && isSuperAdmin;
  const shouldFetchModalClients =
    isFilterModalOpen && (isSuperAdmin || isAgencyScopedUser);

  const derivedAgencyForClients = React.useMemo(() => {
    if (!currentUser) return null;
    if (isSuperAdmin) {
      if (isFilterModalOpen) {
        return (
          draftAdvancedFilters.agency?.value ||
          globalAgencyOption?.value ||
          appliedAdvancedFilters.agency?.value ||
          null
        );
      }
      return (
        appliedAdvancedFilters.agency?.value ||
        globalAgencyOption?.value ||
        selectedFilter ||
        null
      );
    }
    return currentUser.agencyId || null;
  }, [
    appliedAdvancedFilters.agency,
    currentUser,
    draftAdvancedFilters.agency,
    globalAgencyOption,
    isFilterModalOpen,
    isSuperAdmin,
    selectedFilter,
  ]);

  const filterClientsEndpoint = useMemo(() => {
    if (!currentUser) return null;
    const params = new URLSearchParams();
    params.append("ordering", "name");
    if (isSuperAdmin) {
      if (derivedAgencyForClients) {
        params.append("agency", derivedAgencyForClients);
      }
    }
    // Note: For non-superadmin, backend already filters by user's agency/access
    const query = params.toString();
    return `/clients/options/${query ? `?${query}` : ""}`;
  }, [currentUser, derivedAgencyForClients, isSuperAdmin]);

  const { data: filterAgenciesData, isLoading: isLoadingFilterAgencies } =
    getData("/agencies/", interceptorReady && shouldFetchModalAgencies, [
      filterRefresh,
    ]);

  const { data: filterClientsData, isLoading: isLoadingFilterClients } =
    getData(
      shouldFetchModalClients ? filterClientsEndpoint : null,
      interceptorReady &&
      shouldFetchModalClients &&
      Boolean(filterClientsEndpoint),
      [
        filterClientsEndpoint,
        draftAdvancedFilters.agency?.value,
        currentUser?.agencyId,
        filterRefresh,
      ]
    );

  const agencyOptions = useMemo(() => {
    if (!filterAgenciesData) return [];
    return normalizeApiList(filterAgenciesData).map((agency) => ({
      value: agency.id,
      label: agency.name,
    }));
  }, [filterAgenciesData]);

  const clientOptions = useMemo(() => {
    if (!filterClientsData) return [];
    const list = normalizeApiList(filterClientsData);

    const hasAgencySelected = Boolean(
      isFilterModalOpen
        ? draftAdvancedFilters.agency?.value ||
        globalAgencyOption?.value ||
        appliedAdvancedFilters.agency?.value
        : appliedAdvancedFilters.agency?.value || globalAgencyOption?.value
    );

    return list.map((client) => {
      const agencyName =
        client.agency?.name ||
        client.agency_name ||
        client.agency_info?.name ||
        null;

      if (isSuperAdmin && !hasAgencySelected && agencyName) {
        return {
          value: client.id,
          label: `${client.name} - ${agencyName}`,
        };
      }

      return {
        value: client.id,
        label: client.name,
      };
    });
  }, [
    appliedAdvancedFilters.agency,
    draftAdvancedFilters.agency,
    filterClientsData,
    globalAgencyOption,
    isFilterModalOpen,
    isSuperAdmin,
  ]);

  const stateFilterOptions = useMemo(
    () => TASK_STATE_FILTERS.filter((option) => option.value !== "all"),
    []
  );

  const isGlobalAgencyLocked = Boolean(globalAgencyOption);
  const isGlobalClientLocked = Boolean(globalClientOption);

  const filterConfigs = useMemo(() => {
    const configs = [];

    if (isSuperAdmin) {
      configs.push({
        key: "agency",
        title: "Agencia",
        description: "Escoge la agencia para enfocar tus tareas.",
        icon: Building2,
        options: agencyOptions,
        loading: isLoadingFilterAgencies,
        disabled: isGlobalAgencyLocked,
        placeholder: "Selecciona una agencia",
        loadingPlaceholder: "Cargando agencias...",
        fallbackOption: globalAgencyOption,
        role: currentUser?.role,
      });
    }

    if (isSuperAdmin || isAgencyScopedUser) {
      configs.push({
        key: "client",
        title: "Cliente",
        description: "Filtra las tareas asociadas a un cliente.",
        icon: User,
        options: clientOptions,
        loading: isLoadingFilterClients,
        disabled: isGlobalClientLocked,
        placeholder: "Selecciona un cliente",
        loadingPlaceholder: "Cargando clientes...",
        fallbackOption: globalClientOption,
        role: currentUser?.role,
      });
    }

    configs.push({
      key: "state",
      title: "Estado",
      description: "Encuentra tareas según su estado actual.",
      icon: Tag,
      options: stateFilterOptions,
      role: currentUser?.role,
    });

    configs.push({
      key: "ordering",
      title: "Ordenar por",
      description: "Controla cómo se ordenan los resultados.",
      icon: CalendarDays,
      options: TASK_ORDERING_OPTIONS,
      includeAny: false,
      role: currentUser?.role,
    });

    configs.push({
      key: "deadlineRange",
      title: "Rango de deadlines",
      description: "Limita las tareas por fechas de entrega.",
      icon: CalendarDays,
      type: "date-range",
      ui: "calendar-range",
      startKey: "deadlineAfter",
      endKey: "deadlineBefore",
    });

    return configs;
  }, [
    agencyOptions,
    clientOptions,
    currentUser?.role,
    globalAgencyOption,
    globalClientOption,
    isAgencyScopedUser,
    isGlobalAgencyLocked,
    isGlobalClientLocked,
    isLoadingFilterAgencies,
    isLoadingFilterClients,
    isSuperAdmin,
    stateFilterOptions,
  ]);

  const shouldFetchTasks = Boolean(interceptorReady);
  const endpoint = queryString
    ? `dashboard/my-tasks/?${queryString}`
    : "dashboard/my-tasks/";

  const {
    data: tasksResponse,
    error: tasksError,
    isLoading,
    isFetching,
    isInitialLoading,
  } = getData(shouldFetchTasks ? endpoint : null, shouldFetchTasks, [
    dashboardRefreshKey,
    filtersKey,
    tasksRefreshKey,
    taskPage,
    taskPageSize,
  ]);

  const tasksData = useMemo(() => {
    if (!tasksResponse) return { list: [], count: 0 };
    if (Array.isArray(tasksResponse)) {
      return { list: tasksResponse, count: tasksResponse.length };
    }
    if (Array.isArray(tasksResponse.results)) {
      return {
        list: tasksResponse.results,
        count:
          typeof tasksResponse.count === "number"
            ? tasksResponse.count
            : tasksResponse.results.length,
      };
    }
    return {
      list: [],
      count: typeof tasksResponse.count === "number" ? tasksResponse.count : 0,
    };
  }, [tasksResponse]);

  const tasks = tasksData.list;
  const isListLoading = !tasksResponse && (isLoading || isInitialLoading);
  const hasError = Boolean(tasksError);
  React.useEffect(() => {
    setTasksTotalCount(tasksData.count || 0);
  }, [tasksData.count]);
  const activeAdvancedFiltersCount = useMemo(
    () => countActiveAdvancedFilters(appliedAdvancedFilters),
    [appliedAdvancedFilters]
  );
  const hasFiltersApplied = activeAdvancedFiltersCount > 0;
  const prevFiltersKeyRef = React.useRef(filtersKey);
  React.useEffect(() => {
    if (prevFiltersKeyRef.current !== filtersKey) {
      prevFiltersKeyRef.current = filtersKey;
      setTaskPage(0);
    }
  }, [filtersKey]);
  React.useEffect(() => {
    if (!tasksResponse) return;
    const total = tasksData.count || 0;
    if (total === 0) {
      if (taskPage !== 0) setTaskPage(0);
      return;
    }
    const maxPage = Math.max(Math.ceil(total / taskPageSize) - 1, 0);
    if (taskPage > maxPage) {
      setTaskPage(maxPage);
    }
  }, [tasksData.count, taskPageSize, taskPage, tasksResponse]);

  const handleTaskClick = (taskId) => {
    setTaskDetailKey((k) => k + 1);
    setSelectedTaskId(taskId);
  };

  const handleCloseModal = () => {
    setSelectedTaskId(null);
  };

  const handleTaskSuccess = (message) => {
    setSuccessPopup({ isOpen: true, message, error: false });
  };

  const handleTaskError = (message) => {
    setSuccessPopup({ isOpen: true, message, error: true });
  };

  const handleCloseSuccessPopup = () => {
    setSuccessPopup({ isOpen: false, message: "", error: false });
  };

  const handleOpenFilterModal = () => {
    setDraftAdvancedFilters(cloneAdvancedFilters(appliedAdvancedFilters));
    setIsFilterModalOpen(true);
  };

  const handleCloseFilterModal = () => {
    setIsFilterModalOpen(false);
    setDraftAdvancedFilters(cloneAdvancedFilters(appliedAdvancedFilters));
  };

  const handleDraftFilterChange = (field, value) => {
    setDraftAdvancedFilters((prev) => {
      if (field === "deadlineAfter" || field === "deadlineBefore") {
        return { ...prev, [field]: value || "" };
      }
      const nextValue = value
        ? { ...value, isLocked: Boolean(value.isLocked) }
        : null;
      const updated = { ...prev, [field]: nextValue };
      if (field === "agency") {
        updated.client = null;
      }
      return updated;
    });
  };

  const handleClearDraftFilters = () => {
    setDraftAdvancedFilters(
      buildInitialAdvancedFilters(globalAgencyOption, globalClientOption)
    );
  };

  const handleApplyAdvancedFilters = () => {
    setAppliedAdvancedFilters(cloneAdvancedFilters(draftAdvancedFilters));
    setIsFilterModalOpen(false);
  };

  const handleResetFilters = () => {
    setAppliedAdvancedFilters(
      buildInitialAdvancedFilters(globalAgencyOption, globalClientOption)
    );
    setDraftAdvancedFilters(
      buildInitialAdvancedFilters(globalAgencyOption, globalClientOption)
    );
  };

  const handleTaskFilterChange = (filterKey) => {
    if (filterKey === selectedTaskFilter) return;
    setSelectedTaskFilter(filterKey);
  };

  const handleTaskPageChange = (event, newPage) => {
    setTaskPage(newPage);
  };

  const handleTaskRowsPerPageChange = (event) => {
    const newSize = parseInt(event.target.value, 10);
    if (Number.isNaN(newSize)) return;
    setTaskPageSize(newSize);
    setTaskPage(0);
  };

  const renderTaskItem = (task) => {
    const deadlineValue = task.deadline || task.due_date || task.due_at;
    const title = getTaskTitle(task);
    const description =
      getTaskDescription(task) || "Sin descripción disponible";
    const state = getTaskState(task);
    const phaseName = getPhaseName(task);
    const projectName = getProjectName(task);
    // Si el projectName indica que viene de una conversación, mostrar con icono diferente
    const isFromThread = projectName?.startsWith("Thread:");
    let displayProjectName = isFromThread
      ? projectName.replace("Thread: ", "").replace("Thread:", "")
      : projectName;
    // Si es Untitled, mostrar como "Conversación principal"
    if (isFromThread && (!displayProjectName || displayProjectName === "Untitled")) {
      displayProjectName = "Conversación principal";
    }
    const clientName = getClientName(task);
    const deadlineInfo = buildDeadlineInfo(deadlineValue);
    const deadlineDayMonth = formatDeadlineDayMonth(deadlineValue);
    const remainingDaysText = getRemainingDaysText(deadlineInfo);
    const isOverdue = deadlineInfo.tone === "danger";
    const statePresentation = TASK_STATE_BADGES[state] || {
      label: state,
      color: "gray",
    };

    const association =
      projectName || phaseName || clientName || task.associated_with || null;

    return (
      <Box
        key={task.id}
        onClick={() => handleTaskClick(task.id)}
        sx={{
          p: 2,
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: "12px",
          backgroundColor: theme.palette.background.paper,
          display: "flex",
          flexDirection: "column",
          gap: 1,
          cursor: "pointer",
          transition: "background-color 0.2s ease",
          position: "relative",
          "&:hover": {
            backgroundColor: theme.palette.action.hover
          },
        }}
      >
        <Stack
          direction="row"
          spacing={2}
          alignItems="center"
          justifyContent="space-between"
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              {deadlineDayMonth}
              {state !== "finalizado" &&
                remainingDaysText &&
                remainingDaysText === "Vencida" && (
                  <span
                    style={{
                      color: "#dc2626",
                      fontWeight: 400,
                    }}
                  >
                    {" • "}
                    {remainingDaysText}
                  </span>
                )}
              {state !== "finalizado" &&
                remainingDaysText &&
                remainingDaysText !== "Vencida" && (
                  <span
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      color: theme.palette.text.secondary,
                      fontWeight: 400,
                      marginLeft: "8px",
                      gap: "4px",
                    }}
                  >
                    <Clock
                      size={12}
                      style={{ transform: "translateY(0.5px)" }}
                    />
                    <span style={{ transform: "translateY(0.5px)" }}>
                      {remainingDaysText}
                    </span>
                  </span>
                )}
            </Typography>
            {!isOverdue && state !== "finalizado" && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Badge color={statePresentation.color} size="sm">
                  {statePresentation.label}
                </Badge>
                <DocumentationLinkList urls={task.documentation_urls} />
              </Box>
            )}
          </Box>

          {(clientName || displayProjectName) && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              {clientName && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <Users size={14} color={theme.palette.text.secondary} />
                  <Typography
                    variant="caption"
                    sx={{
                      color: theme.palette.text.secondary,
                      fontWeight: 500,
                    }}
                  >
                    {clientName}
                  </Typography>
                </Box>
              )}
              {displayProjectName && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  {isFromThread ? (
                    <MessageSquare size={14} color={theme.palette.text.secondary} />
                  ) : (
                    <FolderOpen size={14} color={theme.palette.text.secondary} />
                  )}
                  <Typography
                    variant="caption"
                    sx={{
                      color: theme.palette.text.secondary,
                      fontWeight: 500,
                    }}
                  >
                    {displayProjectName}
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </Stack>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "auto 1fr",
            columnGap: 1,
            rowGap: 0.25,
            alignItems: "center",
          }}
        >
          <TaskIndicatorDot
            theme={theme}
            sx={{ gridRow: "1", alignSelf: "center" }}
          />
          <Typography
            variant="h6"
            sx={{
              color: theme.palette.text.primary,
              fontWeight: 600,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {title}
          </Typography>

          <Typography
            variant="body2"
            sx={{
              color: theme.palette.text.secondary,
              display: "-webkit-box",
              WebkitLineClamp: 1,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
              textOverflow: "ellipsis",
              mt: 0.25,
              gridColumn: "2",
            }}
          >
            {description}
          </Typography>
        </Box>
      </Box>
    );
  };

  return (
    <>
      <Card
        sx={{
          height: "100%",
          maxHeight: maxHeight,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <CardHeader
          sx={{
            pb: paginationInHeader ? "4px" : undefined,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: { xs: "flex-start", md: "center" },
              justifyContent: "space-between",
              gap: 2,
              flexWrap: "wrap",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <CardTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <ClipboardList size={20} />
                Mis tareas
              </CardTitle>

              <FilterSwitchGroup
                options={Object.entries(TASK_STATUS_FILTERS).map(
                  ([key, config]) => ({
                    value: key,
                    label: config.label,
                  })
                )}
                selectedValue={selectedTaskFilter}
                onChange={handleTaskFilterChange}
                rightContent={
                  <Box sx={{ position: "relative", display: "inline-block" }}>
                    <Tooltip title="Editar filtros" arrow>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleOpenFilterModal}
                        sx={{
                          borderColor: neutrals.gray[200],
                          color: neutrals.gray[600],
                          flexShrink: 0,
                          minWidth: "32px",
                          height: "32px",
                          padding: "4px",
                          backgroundColor: "white",
                        }}
                        aria-label="Abrir filtros avanzados"
                      >
                        <SlidersHorizontal size={16} />
                      </Button>
                    </Tooltip>
                    {hasFiltersApplied && (
                      <Box
                        sx={{
                          position: "absolute",
                          top: "-7px",
                          right: "-7px",
                          minWidth: "18px",
                          height: "18px",
                          borderRadius: "9px",
                          backgroundColor: neutrals.gray[400],
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: "10px",
                          fontWeight: 700,
                          color: "white",
                          boxShadow: "0 1px 3px rgba(0, 0, 0, 0.2)",
                        }}
                      >
                        {activeAdvancedFiltersCount}
                      </Box>
                    )}
                  </Box>
                }
              />
            </Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                flexWrap: "wrap",
                justifyContent: "flex-end",
              }}
            >
              {isFetching && (
                <Typography
                  variant="caption"
                  sx={{ color: theme.palette.text.secondary }}
                >
                  Actualizando...
                </Typography>
              )}
              {paginationInHeader &&
                !isListLoading &&
                tasksTotalCount > 0 && (
                  <TablePagination
                    component="div"
                    count={tasksTotalCount}
                    page={taskPage}
                    onPageChange={handleTaskPageChange}
                    rowsPerPage={taskPageSize}
                    onRowsPerPageChange={handleTaskRowsPerPageChange}
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    labelRowsPerPage="Tareas por página:"
                    labelDisplayedRows={({ from, to, count }) =>
                      `${from}-${to} de ${count !== -1 ? count : `más de ${to}`}`
                    }
                    sx={{
                      width: "auto",
                      maxWidth: "100%",
                      overflow: "hidden",
                      "& .MuiTablePagination-toolbar": {
                        px: { xs: 0.5, md: 1 },
                        minHeight: "18px",
                        height: "18px",
                        flexWrap: { xs: "wrap", md: "nowrap" },
                        gap: { xs: 1, md: 0 },
                      },
                      "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows":
                      {
                        fontSize: {
                          xs: "0.75rem",
                          md: "0.875rem",
                        },
                        whiteSpace: "nowrap",
                      },
                      "& .MuiTablePagination-select": {
                        fontSize: { xs: "0.75rem", md: "0.875rem" },
                      },
                    }}
                  />
                )}
            </Box>
          </Box>
        </CardHeader>
        <CardContent
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
            maxHeight: "100%",
            overflow: "hidden",
            pb: paginationInHeader ? "24px" : 0,
          }}
        >
          {hasError && (
            <Box
              sx={{
                p: 2,
                borderRadius: 2,
                border: `1px solid ${theme.palette.error.light}`,
                backgroundColor: `${theme.palette.error.light}22`,
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <AlertTriangle size={18} color={theme.palette.error.main} />
              <Typography
                variant="body2"
                sx={{ color: theme.palette.error.main }}
              >
                No se pudieron cargar las tareas. Intenta nuevamente o ajusta
                los filtros.
              </Typography>
            </Box>
          )}

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 1.5,
              flex: 1,
              minHeight: 0,
              overflowY: "auto",
              "&::-webkit-scrollbar": {
                width: "8px",
              },
              "&::-webkit-scrollbar-track": {
                backgroundColor: neutrals.gray[100],
                borderRadius: "4px",
              },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: neutrals.gray[300],
                borderRadius: "4px",
                "&:hover": {
                  backgroundColor: neutrals.gray[400],
                },
              },
            }}
          >
            {isListLoading
              ? Array.from({ length: 4 }).map((_, index) => (
                <Box
                  key={`task-skeleton-${index}`}
                  sx={{
                    p: 1.5,
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: "12px",
                  }}
                >
                  <Skeleton variant="text" width="50%" height={22} />
                  <Skeleton variant="text" width="80%" height={18} />
                </Box>
              ))
              : tasks.length > 0
                ? tasks.map((task) => renderTaskItem(task))
                : !hasError && (
                  <EmptyStateMessage
                    icon={ClipboardList}
                    message={
                      hasFiltersApplied
                        ? "No hay tareas que coincidan con los filtros seleccionados."
                        : "No tienes tareas asignadas todavía."
                    }
                    sx={{ py: 4 }}
                  />
                )}
          </Box>

          {!paginationInHeader && !isListLoading && tasksTotalCount > 0 && (
            <TablePagination
              component="div"
              count={tasksTotalCount}
              page={taskPage}
              onPageChange={handleTaskPageChange}
              rowsPerPage={taskPageSize}
              onRowsPerPageChange={handleTaskRowsPerPageChange}
              rowsPerPageOptions={[5, 10, 25, 50]}
              labelRowsPerPage="Filas por página:"
              labelDisplayedRows={({ from, to, count }) =>
                `${from}-${to} de ${count !== -1 ? count : `más de ${to}`}`
              }
              sx={{
                borderTop: `1px solid ${theme.palette.divider}`,
                width: "100%",
                maxWidth: "100%",
                overflow: "hidden",
                "& .MuiTablePagination-toolbar": {
                  px: { xs: 1, md: 2 },
                  flexWrap: { xs: "wrap", md: "nowrap" },
                  gap: { xs: 1, md: 0 },
                  width: "100%",
                  maxWidth: "100%",
                  overflow: "hidden",
                },
                "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows":
                {
                  fontSize: { xs: "0.75rem", md: "0.875rem" },
                  whiteSpace: { xs: "nowrap", md: "nowrap" },
                },
                "& .MuiTablePagination-select": {
                  fontSize: { xs: "0.75rem", md: "0.875rem" },
                },
              }}
            />
          )}
        </CardContent>
      </Card>

      <IncidentFiltersModal
        open={isFilterModalOpen}
        onClose={handleCloseFilterModal}
        title="Filtros avanzados"
        filterConfigs={filterConfigs}
        draftFilters={draftAdvancedFilters}
        onChange={handleDraftFilterChange}
        onApply={handleApplyAdvancedFilters}
        onClearAll={handleClearDraftFilters}
      />

      <TaskDetailModal
        taskId={selectedTaskId}
        open={Boolean(selectedTaskId)}
        onClose={handleCloseModal}
        interceptorReady={interceptorReady}
        setTasksRefreshKey={setTasksRefreshKey}
        detailKey={taskDetailKey}
        onSuccess={handleTaskSuccess}
        onError={handleTaskError}
      />

      <SuccessPopup
        message={successPopup.message}
        isOpen={successPopup.isOpen}
        onClose={handleCloseSuccessPopup}
        error={successPopup.error}
      />
    </>
  );
};

const TaskDetailModal = ({
  open,
  taskId,
  onClose,
  interceptorReady,
  setTasksRefreshKey,
  detailKey = 0,
  onSuccess,
  onError,
}) => {
  const { theme } = useTheme();
  const [isMarkingComplete, setIsMarkingComplete] = useState(false);
  const shouldFetchDetail = Boolean(open && taskId && interceptorReady);

  const {
    data: taskDetail,
    error,
    isLoading,
    isInitialLoading,
  } = getData(
    shouldFetchDetail ? `dashboard/my-tasks/${taskId}/` : null,
    shouldFetchDetail,
    [taskId, detailKey]
  );

  const isLoadingDetail = !taskDetail && (isLoading || isInitialLoading);
  const detail = taskDetail || {};

  React.useEffect(() => {
    const ids = taskDetail?.unopened_notification_ids;
    if (!ids?.length) return;
    apiClient
      .post("dashboard/notifications/mark-opened/", { ids })
      .catch(() => {});
  }, [taskDetail?.id]);
  const phaseName = getPhaseName(detail);
  const rawProjectName = getProjectName(detail);
  const isFromThread = rawProjectName?.startsWith("Thread:");
  let projectName = isFromThread
    ? rawProjectName.replace("Thread: ", "").replace("Thread:", "")
    : rawProjectName;
  // Si es Untitled, mostrar como "Conversación principal"
  if (isFromThread && (!projectName || projectName === "Untitled")) {
    projectName = "Conversación principal";
  }
  const clientName = getClientName(detail);
  const agencyName = getAgencyName(detail);
  const currentState = getTaskState(detail);
  const isAlreadyCompleted = currentState === "finalizado";

  const handleMarkAsComplete = async () => {
    if (isMarkingComplete || !taskId) return;
    setIsMarkingComplete(true);

    try {
      await apiClient.patch(`projects/tasks/${taskId}/update-state/`, {
        state: "finalizado",
      });
      setTasksRefreshKey((prev) => prev + 1);
      onClose();
      onSuccess("Tarea marcada como finalizada exitosamente");
    } catch (err) {
      console.error("Error marcando tarea como finalizada:", err);
      setTasksRefreshKey((prev) => prev + 1);
      onError("No se pudo marcar la tarea como finalizada");
    } finally {
      setIsMarkingComplete(false);
    }
  };

  const metadata = [
    { label: "Cliente", value: clientName, icon: Users },
    { label: "Agencia", value: agencyName, icon: Tag },
    ...(isFromThread
      ? [{ label: "Conversación", value: projectName, icon: MessageSquare }]
      : [{ label: "Proyecto", value: projectName, icon: FolderOpen }]),
    { label: "Fase", value: phaseName, icon: Settings },
    {
      label: "Asignado a",
      value: detail.assigned_to_name || detail.assigned_to?.full_name,
      icon: User,
    },
    {
      label: "Documentación",
      value: <DocumentationLinkList urls={detail.documentation_urls} />,
      icon: Link2,
    },
  ].filter((item) => Boolean(item.value));

  return (
    <UnifiedModal
      open={open}
      onClose={() => onClose()}
      title={getTaskTitle(detail)}
      maxWidth="sm"
    >
      {isLoadingDetail ? (
        <Stack spacing={2}>
          <Skeleton variant="text" width="60%" height={28} />
          <Skeleton variant="text" width="90%" height={20} />
          <Skeleton variant="rectangular" height={120} />
        </Stack>
      ) : error ? (
        <Box
          sx={{
            p: 2,
            borderRadius: 2,
            border: `1px solid ${theme.palette.error.light}`,
            backgroundColor: `${theme.palette.error.light}22`,
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <AlertTriangle size={18} color={theme.palette.error.main} />
          <Typography variant="body2" sx={{ color: theme.palette.error.main }}>
            No se pudo obtener el detalle de la tarea.
          </Typography>
        </Box>
      ) : (
        <Stack spacing={3}>
          {detail.description && (
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                Descripción
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: theme.palette.text.secondary }}
              >
                {detail.description}
              </Typography>
            </Box>
          )}

          <Box sx={{ mb: 2 }}>
            <Typography
              variant="caption"
              sx={{ color: theme.palette.text.secondary }}
            >
              Creado
            </Typography>
            <Typography variant="body1">
              {formatFullDate(detail.created_at)}
            </Typography>
          </Box>

          <Stack direction="row" spacing={2}>
            <Box sx={{ flex: 1 }}>
              <Typography
                variant="caption"
                sx={{ color: theme.palette.text.secondary }}
              >
                Deadline
              </Typography>
              <Typography variant="body1">
                {formatDateOnly(detail.deadline)}
              </Typography>
            </Box>
            {detail.completed_at && (
              <Box sx={{ flex: 1 }}>
                <Typography
                  variant="caption"
                  sx={{ color: theme.palette.text.secondary }}
                >
                  Finalizado
                </Typography>
                <Typography variant="body1">
                  {formatFullDate(detail.completed_at)}
                </Typography>
              </Box>
            )}
          </Stack>

          {metadata.length > 0 && (
            <Stack spacing={1.5}>
              {metadata.map((item) => (
                <Box
                  key={item.label}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <item.icon size={16} color={theme.palette.text.secondary} />
                  <Typography
                    variant="body2"
                    sx={{ color: theme.palette.text.secondary, minWidth: 100 }}
                  >
                    {item.label}:
                  </Typography>
                  <Typography variant="body2">{item.value}</Typography>
                </Box>
              ))}
            </Stack>
          )}

          <Stack
            direction="row"
            spacing={1}
            flexWrap="wrap"
            justifyContent="space-between"
            alignItems="center"
          >
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
              {detail.google_calendar_link && (
                <Button
                  variant="outline"
                  size="sm"
                  component="a"
                  href={detail.google_calendar_link}
                  target="_blank"
                  rel="noopener noreferrer"
                  startIcon={<CalendarDays size={16} />}
                >
                  Abrir en Google Calendar
                </Button>
              )}
            </Box>
            {!isAlreadyCompleted && (
              <Button
                variant="default"
                size="sm"
                onClick={handleMarkAsComplete}
                disabled={isMarkingComplete}
                startIcon={<CheckCircle size={16} />}
              >
                {isMarkingComplete ? "Marcando..." : "Marcar como finalizado"}
              </Button>
            )}
          </Stack>
        </Stack>
      )}
    </UnifiedModal>
  );
};

export { TaskDetailModal };
